import * as React from "react"
import BottomBar from "./BottomBar"
import ElementHighlighter from "./ElementHighlighter"
import ElementPickerBar from "./ElementPickerBar"
import StepEditorPopover from "./StepEditorPopover"
import { TourBuilderController } from "../controllers"
import { TourRunner } from "../helpers"
import { Widget, WidgetTour, WidgetTourStep, WidgetTourStepType } from "../../types"

interface IProps {
  token: string
  onExit: () => void
}

type Mode = "idle" | "picking" | "editing"

interface IState {
  loading: boolean
  widget: Widget | null
  tour: WidgetTour | null
  activeTab: "steps" | "settings"
  mode: Mode
  editingStep: WidgetTourStep | null
  anchorRect: DOMRect | null
  saving: boolean
  hoveredSelector: string | null
}

class TourBuilderRoot extends React.Component<IProps, IState> {
  state: IState = {
    loading: true,
    widget: null,
    tour: null,
    activeTab: "steps",
    mode: "idle",
    editingStep: null,
    anchorRect: null,
    saving: false,
    hoveredSelector: null,
  }

  componentDidMount() {
    this.fetchTour()
  }

  fetchTour = async () => {
    try {
      const response = await TourBuilderController.getTour(this.props.token)
      this.setState({ widget: response.widget, tour: response.tour, loading: false })
    } catch (ex) {
      console.error(ex)
      this.setState({ loading: false })
    }
  }

  notifyOpener = () => {
    window.opener?.postMessage({ type: "tour_builder_saved" }, "*")
  }

  onTabChange = (activeTab: "steps" | "settings") => {
    this.setState({ activeTab })
  }

  onAddStepClick = (type: WidgetTourStepType) => {
    const steps = this.state.tour?.steps || []
    const position = steps.length > 0 ? Math.max(...steps.map((s) => s.position || 0)) + 1 : 1
    const editingStep: WidgetTourStep = { type, position, page_path: window.location.pathname }

    // POST steps render as a centered modal with no anchor element, so they
    // skip element-picking entirely; POINTER/VIDEO_POINTER require picking
    // an element on the page first.
    if (type === WidgetTourStepType.POST) {
      this.setState({ mode: "editing", editingStep, anchorRect: null })
    } else {
      this.setState({ mode: "picking", editingStep, anchorRect: null })
    }
  }

  onStepClick = (step: WidgetTourStep) => {
    // The step's element may live on a different page_path than the one
    // we're currently on, so there's nothing on screen to anchor to here -
    // center the popover instead of trying to locate a live element.
    this.setState({ mode: "editing", editingStep: { ...step }, anchorRect: null })
  }

  onChangeElementClick = () => {
    this.setState({ mode: "picking" })
  }

  onElementHover = (selector: string | null) => {
    this.setState({ hoveredSelector: selector })
  }

  onElementPicked = (selector: string, element: Element) => {
    this.setState((current) => ({
      mode: "editing",
      anchorRect: element.getBoundingClientRect(),
      editingStep: { ...current.editingStep, element: selector, page_path: window.location.pathname },
      hoveredSelector: null,
    }))
  }

  onPickCancel = () => {
    this.setState((current) => ({
      mode: current.editingStep?.id ? "editing" : "idle",
      hoveredSelector: null,
    }))
  }

  onStepChange = (step: WidgetTourStep) => {
    this.setState({ editingStep: step })
  }

  onStepCancel = () => {
    this.setState({ mode: "idle", editingStep: null, anchorRect: null })
  }

  onStepSave = async () => {
    const { editingStep } = this.state
    if (!editingStep || !editingStep.title) return

    this.setState({ saving: true })

    try {
      const saved = editingStep.id
        ? await TourBuilderController.updateStep(this.props.token, editingStep)
        : await TourBuilderController.createStep(this.props.token, editingStep)

      this.setState((current) => {
        const steps = current.tour?.steps || []
        const nextSteps = editingStep.id
          ? steps.map((step) => (step.id === saved.id ? saved : step))
          : [...steps, saved]

        return {
          tour: { ...current.tour, steps: nextSteps },
          mode: "idle",
          editingStep: null,
          anchorRect: null,
          saving: false,
        }
      })

      this.notifyOpener()
    } catch (ex) {
      console.error(ex)
      this.setState({ saving: false })
    }
  }

  onRemoveStepClick = async (step: WidgetTourStep) => {
    if (!step.id) return

    this.setState((current) => ({
      tour: { ...current.tour, steps: (current.tour?.steps || []).filter((s) => s.id !== step.id) },
    }))

    try {
      await TourBuilderController.deleteStep(this.props.token, step.id)
      this.notifyOpener()
    } catch (ex) {
      console.error(ex)
      this.fetchTour()
    }
  }

  onReorder = async (orderedIds: string[]) => {
    this.setState((current) => {
      const stepsById = new Map((current.tour?.steps || []).map((step) => [step.id, step]))
      const reordered = orderedIds
        .map((id, index) => {
          const step = stepsById.get(id)
          return step ? { ...step, position: index + 1 } : null
        })
        .filter(Boolean) as WidgetTourStep[]

      return { tour: { ...current.tour, steps: reordered } }
    })

    try {
      await TourBuilderController.reorderSteps(this.props.token, orderedIds)
      this.notifyOpener()
    } catch (ex) {
      console.error(ex)
      this.fetchTour()
    }
  }

  onTourChange = async (update: Partial<WidgetTour>) => {
    this.setState((current) => ({ tour: { ...current.tour, ...update } }))

    try {
      const saved = await TourBuilderController.updateTour(this.props.token, update)
      this.setState((current) => ({ tour: { ...current.tour, ...saved } }))
      this.notifyOpener()
    } catch (ex) {
      console.error(ex)
    }
  }

  onPreviewClick = () => {
    const { widget, tour } = this.state
    if (widget && tour) TourRunner.start(widget, tour)
  }

  onCancelClick = () => {
    this.props.onExit()
  }

  onSaveAndCloseClick = () => {
    this.notifyOpener()
    this.props.onExit()
  }

  render() {
    const { loading, tour, activeTab, mode, editingStep, anchorRect, saving, hoveredSelector } = this.state
    if (loading || !tour) return null

    return (
      <div className="bizzey-tb-root">
        {mode === "picking" && (
          <ElementHighlighter onPick={this.onElementPicked} onCancel={this.onPickCancel} onHover={this.onElementHover} />
        )}

        {mode === "editing" && editingStep && (
          <StepEditorPopover
            step={editingStep}
            anchorRect={anchorRect}
            saving={saving}
            onChange={this.onStepChange}
            onSave={this.onStepSave}
            onCancel={this.onStepCancel}
            onChangeElement={this.onChangeElementClick}
          />
        )}

        {mode === "picking" ? (
          <ElementPickerBar selector={hoveredSelector} onCancel={this.onPickCancel} />
        ) : (
          <BottomBar
            tour={tour}
            activeTab={activeTab}
            onTabChange={this.onTabChange}
            onAddStepClick={this.onAddStepClick}
            onStepClick={this.onStepClick}
            onRemoveStepClick={this.onRemoveStepClick}
            onReorder={this.onReorder}
            onTourChange={this.onTourChange}
            onPreviewClick={this.onPreviewClick}
            onCancelClick={this.onCancelClick}
            onSaveAndCloseClick={this.onSaveAndCloseClick}
          />
        )}
      </div>
    )
  }
}

export default TourBuilderRoot
